'use client';

import { createContext, useCallback, useContext, useMemo, useRef } from 'react';

import { useFetchFeed, useItemDetails } from '@/hooks';
import { IData } from '@/hooks/useFetchFeed';
import { IItem, IPaginatedItem } from '@/types/feed.types';

import { IFeedData } from './Feed.types';

interface ContextProps {
  contextValue: IFeedData;
  children: React.ReactNode;
}

interface IFeedContext extends IData {
  selectedIndex: number;
  itemList: IPaginatedItem[];
  handleRefetch?: () => Promise<any>;
}

const FeedContext = createContext<IFeedContext>({
  itemList: [],
  selectedIndex: 0,
  sectionTitle: '',
  fetchNextPage: undefined,
  hasNextPage: false,
  isLoading: false,
  isFetching: false,
  refetch: undefined,
  handleRefetch: undefined,
  error: null,
  isError: false,
});

export const FeedContextProvider: React.FC<ContextProps> = ({
  contextValue,
  children,
}) => {
  const singleItemId = useRef<string | null>(null);

  const {
    itemId,
    shouldFetchItemsList,
    itemDetails: initialItemDetails,
  } = contextValue;
  const { itemDetails, refetch: refetchItem } = useItemDetails(itemId);

  // Only use initialItemDetails if it matches the current itemId
  const validInitialItemDetails = useMemo(() => {
    if (!initialItemDetails) return null;
    const isValid =
      initialItemDetails.id === itemId ||
      initialItemDetails.sanitizedTitle === itemId;

    return isValid ? initialItemDetails : null;
  }, [initialItemDetails, itemId]);

  const {
    itemList,
    sectionTitle,
    fetchNextPage,
    hasNextUrl,
    hasNextPage,
    isLoading,
    isFetching,
    refetch,
    error,
    isError,
  } = useFetchFeed(contextValue);

  const handleRefetch = useCallback(async () => {
    if (!refetch && !refetchItem) return;

    // Refetch both item details and feed data
    const [itemResult, feedResult] = await Promise.all([
      refetchItem?.(),
      refetch?.(),
    ]);

    return { itemResult, feedResult };
  }, [refetch, refetchItem]);

  const sliderData = useMemo(() => {
    if (!shouldFetchItemsList) {
      return {
        selectedIndex: 0,
        itemList: [itemDetails ?? validInitialItemDetails],
      };
    }

    if (
      !itemList.length ||
      !itemId ||
      (!itemDetails && !validInitialItemDetails) ||
      isLoading
    ) {
      return {
        selectedIndex: 0,
        itemList,
      };
    }

    const currentItemDetails = itemDetails ?? validInitialItemDetails;

    const filteredItemsList = singleItemId.current
      ? itemList.filter(
          (item: IItem) =>
            item.id !== singleItemId.current &&
            item.sanitizedTitle !== singleItemId.current,
        )
      : itemList;

    const currentIndex = filteredItemsList.findIndex(
      (item) => item.id === itemId || item.sanitizedTitle === itemId,
    );

    if (currentIndex === -1) {
      singleItemId.current = itemId;

      return {
        selectedIndex: 0,
        itemList: [currentItemDetails, ...filteredItemsList],
      };
    } else {
      return {
        selectedIndex: currentIndex,
        itemList: singleItemId.current
          ? [currentItemDetails, ...filteredItemsList]
          : filteredItemsList,
      };
    }
  }, [
    isLoading,
    itemList,
    itemDetails,
    validInitialItemDetails,
    itemId,
    shouldFetchItemsList,
    error,
    isError,
  ]);

  const memoizedContextValue: IFeedContext = useMemo(() => {
    return {
      ...sliderData,
      sectionTitle,
      fetchNextPage,
      hasNextPage,
      hasNextUrl,
      isLoading,
      refetch,
      handleRefetch,
      isFetching,
      error,
      isError,
      itemList: sliderData.itemList.filter(
        (item): item is IPaginatedItem => item !== null,
      ),
    };
  }, [
    sliderData,
    sectionTitle,
    hasNextUrl,
    fetchNextPage,
    hasNextPage,
    isLoading,
    refetch,
    handleRefetch,
    isFetching,
    error,
    isError,
  ]);

  return (
    <FeedContext.Provider value={memoizedContextValue}>
      {children}
    </FeedContext.Provider>
  );
};

export function useFeedContext() {
  const context = useContext(FeedContext);
  if (!context) {
    throw new Error('useFeedContext must be used within a FeedContextProvider');
  }
  return context;
}
